import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MAX_WORKSPACE_NAME } from "@helm/core";
import { requestFlattenAll } from "../trading/actions";
import { MODULE_BY_KIND, TEMPLATES, WIDGET_TITLES, type WorkspaceTemplate } from "./templates";
import "./workspacePicker.css";

export const DESCRIPTIONS: Readonly<Record<string, string>> = {
  tradovate: "Two charts with a ladder and account tables.",
  scalper: "One chart, execution controls and a tabbed account dock.",
  "daily-trading": "Chart, daily checklist, notes and execution controls.",
  ladder: "One instrument with a ladder, watchlist and tape.",
  "order-flow": "Chart, tape and ladder together.",
  "prop-eval": "Chart, ladder, account information and daily journal.",
  "multi-chart": "Four charts and a compact execution pad.",
  "chart-grid": "Four chart panes inside one module, plus execution controls.",
  replay: "Chart, ladder, tape and journal; replay transport is not available yet.",
  "glacis-single": "Chart, watchlist, tape, ladder and trading tables.",
  "glacis-multi": "Multiple charts, market data, account tables and order ticket.",
  "chart-focus": "A large chart with execution and account modules in dock tabs.",
  empty: "An empty workspace. Add the modules you want.",
};

/** Module schematic only: no market data, subscriptions, account values or saved state. */
export function ModulePreview({ template }: { template: WorkspaceTemplate }): React.JSX.Element {
  return (
    <div className="workspace-preset-modules" aria-label="Module schematic">
      {template.panels.length === 0 ? (
        <span className="workspace-preset-module">Empty canvas</span>
      ) : (
        template.panels.map((panel) => (
          <span key={panel.id} className="workspace-preset-module" data-module={panel.kind}>
            {WIDGET_TITLES[panel.kind]}
          </span>
        ))
      )}
    </div>
  );
}

/** Additive creation; browsing never mounts another live workspace or changes appearance. */
export function WorkspacePicker({
  disabled,
  onCreate,
  onClose,
}: {
  disabled: boolean;
  onCreate: (template: string, name: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  const id = useId();
  const [selected, setSelected] = useState(TEMPLATES[0]?.id ?? "tradovate");
  const [name, setName] = useState(TEMPLATES[0]?.name ?? "Workspace");
  const template = TEMPLATES.find((t) => t.id === selected);
  const dialog = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const submitted = useRef(false);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    close.current?.focus();
    return () => {
      const target =
        previous?.isConnected && previous !== document.body
          ? previous
          : document.querySelector<HTMLElement>('button[aria-label="New workspace"]');
      target?.focus();
    };
  }, []);
  return createPortal(
    <div
      className="workspace-picker-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialog}
        className="workspace-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onClose();
          } else if (event.key === "Tab") {
            const items = [
              ...(dialog.current?.querySelectorAll<HTMLElement>(
                'button:not([disabled]), input:not([disabled]), [tabindex="0"]',
              ) ?? []),
            ].filter((element) => element.getClientRects().length > 0);
            const first = items[0];
            const last = items.at(-1);
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          } else if (
            event.ctrlKey &&
            event.shiftKey &&
            !event.altKey &&
            !event.metaKey &&
            event.code === "KeyF"
          ) {
            event.preventDefault();
            onClose();
            requestFlattenAll();
          }
          event.stopPropagation();
        }}
      >
        <header className="workspace-picker-head">
          <h2 id={`${id}-title`}>New workspace</h2>
          <button ref={close} className="btn btn-sm" type="button" onClick={onClose}>
            Cancel
          </button>
        </header>
        <p id={`${id}-description`} className="workspace-picker-description">
          Choose a layout. Your existing workspaces and appearance stay as they are. Module schematics show
          contents, not exact panel sizes.
        </p>
        <div className="workspace-presets" role="radiogroup" aria-label="Workspace preset">
          {TEMPLATES.map((t) => {
            const later = [
              ...new Set(
                t.panels.filter((p) => MODULE_BY_KIND.get(p.kind)?.later).map((p) => WIDGET_TITLES[p.kind]),
              ),
            ];
            return (
              <label
                key={t.id}
                className={selected === t.id ? "workspace-preset selected" : "workspace-preset"}
              >
                <span className="workspace-preset-title">
                  <input
                    type="radio"
                    name={`${id}-preset`}
                    value={t.id}
                    checked={selected === t.id}
                    onChange={() => {
                      setSelected(t.id);
                      setName(t.name);
                    }}
                  />
                  {t.name}
                </span>
                <ModulePreview template={t} />
                <span className="workspace-preset-description">
                  {DESCRIPTIONS[t.id] ?? "Workspace modules"}
                </span>
                {later.length > 0 && (
                  <span className="workspace-preset-later">Not yet available: {later.join(", ")}</span>
                )}
              </label>
            );
          })}
        </div>
        <footer className="workspace-picker-footer">
          <label htmlFor={`${id}-name`}>Workspace name</label>
          <input
            id={`${id}-name`}
            className="input"
            maxLength={MAX_WORKSPACE_NAME}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          <button
            type="button"
            className="btn btn-primary"
            disabled={disabled || !template}
            onClick={() => {
              if (disabled || !template || submitted.current) return;
              submitted.current = true;
              onCreate(template.id, name.trim() || template.name);
              onClose();
            }}
          >
            Create workspace
          </button>
          {disabled && <span role="status">Workspace limit reached. Existing workspaces are preserved.</span>}
        </footer>
      </div>
    </div>,
    document.body,
  );
}
